// Shared metric definitions used by ComparisonTable, PDF export, and Excel export.

import type { TaxScenario } from "@/tax/types";

export interface MetricRow {
  label: string;
  key: string;
  getValue: (s: TaxScenario) => number;
  format: "currency" | "percent";
  highlight?: boolean;
  optional?: boolean;
  direction: "lower-is-better" | "higher-is-better";
  group?: "current" | "future";
}

/** Rows saved before stateWithholding existed carry neither the input nor the engine result. */
const stateWithheld = (s: TaxScenario) => s.input.stateWithholding ?? 0;
const netStateOwed = (s: TaxScenario) =>
  s.results.netStateTaxOwedOrRefund ?? s.results.stateIncomeTax - stateWithheld(s);

export const METRICS: MetricRow[] = [
  {
    label: "AGI",
    key: "agi",
    getValue: (s) => s.results.adjustedGrossIncome,
    format: "currency",
    direction: "lower-is-better",
  },
  {
    label: "Roth Conversion Amount",
    key: "roth",
    getValue: (s) => s.input.rothConversionAmount,
    format: "currency",
    optional: true,
    direction: "higher-is-better",
  },
  {
    label: "Alt Investment",
    key: "alt",
    getValue: (s) => s.results.alternativeInvestmentCapital,
    format: "currency",
    direction: "lower-is-better",
  },
  {
    label: "Total Economic Cost",
    key: "eco",
    getValue: (s) => s.results.totalEconomicCost,
    format: "currency",
    highlight: true,
    direction: "lower-is-better",
  },
  {
    label: "Deduction Used",
    key: "ded",
    getValue: (s) => s.results.totalDeduction,
    format: "currency",
    direction: "higher-is-better",
  },
  {
    label: "Taxable Income",
    key: "ti",
    getValue: (s) => s.results.taxableIncome,
    format: "currency",
    direction: "lower-is-better",
  },
  {
    label: "Itemized Deductions",
    key: "item",
    getValue: (s) => s.results.itemizedDeductions,
    format: "currency",
    direction: "higher-is-better",
  },
  {
    label: "Taxable Social Security",
    key: "tss",
    getValue: (s) => s.results.taxableSocialSecurity,
    format: "currency",
    direction: "lower-is-better",
  },
  {
    label: "Ordinary Income Tax",
    key: "oit",
    getValue: (s) => s.results.ordinaryIncomeTax,
    format: "currency",
    direction: "lower-is-better",
  },
  {
    label: "CG/Dividends Tax",
    key: "cg",
    getValue: (s) => s.results.capitalGainsDividendsTax,
    format: "currency",
    optional: true,
    direction: "lower-is-better",
  },
  {
    label: "AMT Owed",
    key: "amt",
    getValue: (s) => s.results.amtOwed,
    format: "currency",
    optional: true,
    direction: "lower-is-better",
  },
  { label: "NIIT", key: "niit", getValue: (s) => s.results.niit, format: "currency", optional: true, direction: "lower-is-better" },
  {
    label: "SE Tax",
    key: "se",
    getValue: (s) => s.results.selfEmploymentTax,
    format: "currency",
    optional: true,
    direction: "lower-is-better",
  },
  {
    label: "Total Federal Tax",
    key: "fed",
    getValue: (s) => s.results.totalFederalTax,
    format: "currency",
    highlight: true,
    direction: "lower-is-better",
  },
  // Payments sit directly under the total they come off, so each side subtracts
  // top-to-bottom on screen. All optional: a client who paid nothing shows neither row.
  {
    label: "Federal Withholding",
    key: "paidFedW",
    getValue: (s) => s.input.federalWithholding,
    format: "currency",
    optional: true,
    direction: "higher-is-better",
  },
  {
    label: "Federal Estimated Payments",
    key: "paidFedEst",
    getValue: (s) => s.input.estimatedTaxPayments,
    format: "currency",
    optional: true,
    direction: "higher-is-better",
  },
  {
    label: "Paid with Extension",
    key: "paidExt",
    getValue: (s) => s.input.amountPaidWithExtension,
    format: "currency",
    optional: true,
    direction: "higher-is-better",
  },
  {
    label: "Federal Tax Owed/Refund",
    key: "net",
    getValue: (s) => s.results.netTaxOwedOrRefund,
    format: "currency",
    highlight: true,
    direction: "lower-is-better",
  },
  {
    label: "State Income Tax",
    key: "state",
    getValue: (s) => s.results.stateIncomeTax,
    format: "currency",
    highlight: true,
    direction: "lower-is-better",
  },
  {
    label: "State Tax Withheld",
    key: "paidState",
    getValue: stateWithheld,
    format: "currency",
    optional: true,
    direction: "higher-is-better",
  },
  {
    label: "Net State Tax Owed/Refund",
    key: "netState",
    getValue: netStateOwed,
    format: "currency",
    highlight: true,
    direction: "lower-is-better",
  },
  {
    label: "Total Tax (Fed+State)",
    key: "total",
    getValue: (s) => s.results.totalTaxLiability,
    format: "currency",
    highlight: true,
    direction: "lower-is-better",
  },
  {
    label: "Effective Tax Rate",
    key: "rate",
    getValue: (s) => s.results.effectiveTaxRate,
    format: "percent",
    highlight: true,
    direction: "lower-is-better",
  },
  {
    label: "Medicare Part B (Annual)",
    key: "mb",
    getValue: (s) => s.results.medicarePartBPremium * 12,
    format: "currency",
    optional: true,
    direction: "lower-is-better",
  },
  {
    label: "Medicare Part D (Annual)",
    key: "md",
    getValue: (s) => s.results.medicarePartDPremium * 12,
    format: "currency",
    optional: true,
    direction: "lower-is-better",
  },
  {
    label: "Roth Future Value",
    key: "rfv",
    getValue: (s) => s.results.rothFutureValue,
    format: "currency",
    direction: "higher-is-better",
    group: "future",
  },
  {
    label: "Est. Future Taxes Avoided",
    key: "fta",
    getValue: (s) => s.results.estimatedFutureTaxAvoided,
    format: "currency",
    direction: "higher-is-better",
    group: "future",
  },
  {
    label: "Alt Distributions Above Capital",
    key: "hti",
    getValue: (s) => s.results.hpeTotalInterest,
    format: "currency",
    direction: "higher-is-better",
    group: "future",
  },
  {
    label: "Alt Future Value",
    key: "hfv",
    getValue: (s) => s.results.hpeFutureValue,
    format: "currency",
    direction: "higher-is-better",
    group: "future",
  },
];
